/* Shared SEO core (CommonJS): used by the React app, the dev server and the build script. */
const registry = require('./seoRegistry.json');

const { site, pages, redirects, noindex_prefixes: noindexPrefixes, noindex_follow_prefixes: noindexFollowPrefixes = [], unpublished_paths: unpublishedPaths = [] } = registry;

const normalize = (path) => (path.length > 1 ? path.replace(/\/+$/, '') : path) || '/';
const abs = (base, url) => (!url ? url : url.startsWith('/') ? `${base}${url}` : url);
const redirectFor = (path) => (redirects.find((r) => r.from === normalize(path)) || {}).to || null;
const isNoindex = (path) => noindexPrefixes.some((p) => normalize(path).startsWith(p));

// Company Profile (public view) overrides the registry's built-in business identity; registry values stay as the Apollo defaults.
const DEFAULTS = JSON.parse(JSON.stringify(site));
let brand = null;
const telFmt = (p) => { const d = (p || '').replace(/\D/g, ''); return d.length === 10 ? `+1-${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : p || undefined; };
const applyBrand = (p) => {
  if (!p || typeof p !== 'object') return;
  brand = p;
  const name = p.brand_name || '';
  const same = name === DEFAULTS.name;
  const a = p.address || {};
  const street = [a.street, a.suite].filter(Boolean).join(', ');
  Object.assign(site, {
    name,
    suffix: name ? ` | ${name}` : '',
    legal_name: same ? DEFAULTS.legal_name : p.legal_name || undefined,
    email: p.public_email || undefined,
    telephone: telFmt(p.phone),
    address: street ? { streetAddress: street, addressLocality: a.city, addressRegion: a.state, postalCode: a.zip, addressCountry: a.country || 'US' } : undefined,
    same_as: same ? DEFAULTS.same_as : [],
    default_title: brandTitle(DEFAULTS.default_title),
    default_image: same ? DEFAULTS.default_image : p.logo_url || DEFAULTS.default_image,
  });
};
// Swaps only the trailing " | <business>" suffix; titles that mention the business elsewhere are intentional SEO copy.
function brandTitle(t) { return t && t.endsWith(DEFAULTS.suffix) ? t.slice(0, -DEFAULTS.suffix.length) + site.suffix : t; }
if (typeof window !== 'undefined' && window.__SITE_BRAND__) applyBrand(window.__SITE_BRAND__);

const orgId = (base) => `${base}/#organization`;
// Object.assign (not object spread): spread makes production Babel inject an ESM helper import, which breaks this CommonJS module at runtime.
const address = () => site.address && Object.assign({ '@type': 'PostalAddress' }, site.address);
const areaServed = () => (site.area_served || ['New York']).map((name) => ({ '@type': 'AdministrativeArea', name }));

const schemaFor = (path, entry, base) => {
  if (!entry) return null;
  const url = `${base}${path === '/' ? '/' : path}`;
  if (entry.schema === 'home') {
    return {
      '@context': 'https://schema.org',
      '@graph': [
        { '@type': ['Organization', 'LocalBusiness'], '@id': orgId(base), name: site.name, legalName: site.legal_name, url: `${base}/`, email: site.email, telephone: site.telephone, image: abs(base, site.default_image), address: address(), areaServed: areaServed(), sameAs: site.same_as },
        { '@type': 'WebSite', '@id': `${base}/#website`, url: `${base}/`, name: site.name, publisher: { '@id': orgId(base) } },
      ],
    };
  }
  if (entry.schema === 'collection') return { '@context': 'https://schema.org', '@type': 'CollectionPage', name: brandTitle(entry.title), url, isPartOf: { '@id': `${base}/#website` }, about: { '@id': orgId(base) } };
  if (entry.schema === 'article') return { '@context': 'https://schema.org', '@type': 'BlogPosting', headline: brandTitle(entry.title), description: entry.description, image: abs(base, entry.image), url, datePublished: entry.published, dateModified: entry.modified || entry.published, author: { '@id': orgId(base) }, publisher: { '@id': orgId(base) } };
  if (entry.schema === 'contact') return { '@context': 'https://schema.org', '@type': 'ContactPage', name: brandTitle(entry.title), url, about: { '@id': orgId(base) } };
  // Page-specific Service (e.g. local landing pages with their own areaServed / audience).
  if (entry.service_schema) return Object.assign({ '@context': 'https://schema.org', '@type': 'Service', description: entry.description, url, provider: { '@id': orgId(base) } }, entry.service_schema);
  if (entry.service) return { '@context': 'https://schema.org', '@type': 'Service', name: entry.service, serviceType: entry.service, description: entry.description, url, provider: { '@id': orgId(base) }, areaServed: areaServed() };
  return { '@context': 'https://schema.org', '@type': entry.page_type || 'WebPage', name: brandTitle(entry.title), description: entry.description, url, isPartOf: { '@id': `${base}/#website` }, about: { '@id': orgId(base) } };
};

// Resolved SEO for a path (falls back to site defaults).
const seoFor = (rawPath, base, override) => {
  const path = normalize(rawPath);
  // Unpublished drafts get a "not found" head from the first byte: no homepage title, canonical or schema.
  if (unpublishedPaths.includes(path)) {
    return { known: false, title: brandTitle('Page Not Found'), description: 'This page is not available.', image: abs(base, site.default_image), canonical: null, noindex: true, nofollow: true, schema: null };
  }
  const entry = override || pages[path];
  const nofollow = isNoindex(path);
  // Unregistered pages under these prefixes (e.g. thin smart-home platform pages) stay reachable but are "noindex, follow".
  const noindex = nofollow || (!entry && noindexFollowPrefixes.some((p) => path.startsWith(p)));
  return {
    known: !!entry,
    title: brandTitle(entry ? entry.title : site.default_title),
    description: entry ? entry.description : site.default_description,
    image: abs(base, (entry && entry.image) || site.default_image),
    canonical: entry ? `${base}${path === '/' ? '/' : path}` : null,
    noindex,
    nofollow,
    schema: schemaFor(path, entry, base),
  };
};

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const headTags = (path, base, override) => {
  const s = seoFor(path, base, override);
  const tags = [
    `<title>${esc(s.title)}</title>`,
    `<meta name="description" content="${esc(s.description)}" />`,
    s.noindex && `<meta name="robots" content="${s.nofollow ? 'noindex, nofollow' : 'noindex, follow'}" />`,
    s.canonical && `<link rel="canonical" href="${esc(s.canonical)}" data-cms="1" />`,
    site.name && `<meta property="og:site_name" content="${esc(site.name)}" />`,
    '<meta property="og:type" content="website" />',
    `<meta property="og:title" content="${esc(s.title)}" />`,
    `<meta property="og:description" content="${esc(s.description)}" />`,
    `<meta property="og:image" content="${esc(s.image)}" />`,
    s.canonical && `<meta property="og:url" content="${esc(s.canonical)}" />`,
    '<meta name="twitter:card" content="summary_large_image" />',
    `<meta name="twitter:title" content="${esc(s.title)}" />`,
    `<meta name="twitter:description" content="${esc(s.description)}" />`,
    `<meta name="twitter:image" content="${esc(s.image)}" />`,
    s.schema && `<script type="application/ld+json" data-route-ld="1">${JSON.stringify(s.schema).replace(/</g, '\\u003c')}</script>`,
    brand && `<script data-site-brand="1">window.__SITE_BRAND__=${JSON.stringify(brand).replace(/</g, '\\u003c')}</script>`,
  ];
  return tags.filter(Boolean).join('\n        ');
};

const SEO_BLOCK = /<!-- SEO:START -->[\s\S]*?<!-- SEO:END -->/;
// Minified builds drop the SEO comment markers, so strip existing tags and re-insert.
const STRIP = /<title>[\s\S]*?<\/title>|<meta (?:name="(?:description|robots|twitter:[a-z]+)"|property="og:[a-z_:]+")[^>]*>|<link rel="canonical"[^>]*>|<script data-site-brand[^>]*>[\s\S]*?<\/script>/g;
const injectHead = (html, path, base, override) => (SEO_BLOCK.test(html)
  ? html.replace(SEO_BLOCK, `<!-- SEO:START -->\n        ${headTags(path, base, override)}\n        <!-- SEO:END -->`)
  : html.replace(STRIP, '').replace(/<head>/, `<head>${headTags(path, base, override)}`));

// Registry-style entry for a published blog post (build + dev server prerender its head).
const withBrand = (t) => { const b = brandTitle(t); return !site.name || b.toLowerCase().includes(site.name.toLowerCase()) ? b : `${b}${site.suffix}`; };
const blogEntry = (p) => ({
  title: withBrand(p.meta_title || p.title), description: p.meta_description || p.excerpt || site.default_description,
  image: p.featured_image || site.default_image, schema: 'article', published: (p.published_at || '').slice(0, 10) || undefined, modified: (p.updated_at || '').slice(0, 10) || undefined,
});

const sitemapPaths = () => Object.keys(pages).filter((p) => !isNoindex(p));
const sitemapXml = (base, extra = []) => {
  const urls = [...sitemapPaths().map((p) => ({ loc: `${base}${p === '/' ? '/' : p}`, priority: pages[p].priority || '0.6' })), ...extra];
  const body = urls.map((u) => `  <url><loc>${esc(u.loc)}</loc>${u.lastmod ? `<lastmod>${u.lastmod}</lastmod>` : ''}<priority>${u.priority}</priority></url>`).join('\n');
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${body}\n</urlset>\n`;
};

const robotsTxt = (base) => [
  'User-agent: *',
  'Allow: /',
  ...['/admin', '/checkout', '/dashboard', '/my-', '/saved', '/installer', '/customer-portal', '/preview/', '/feedback/', '/pay/', '/compare-fabrics', '/products/compare', '/configure', '/login', '/register'].map((p) => `Disallow: ${p}`),
  '',
  `Sitemap: ${base}/sitemap.xml`,
  '',
].join('\n');

module.exports = { applyBrand, brandTitle, blogEntry, withBrand, registry, site, pages, redirects, unpublishedPaths, normalize, abs, seoFor, headTags, injectHead, sitemapXml, sitemapPaths, robotsTxt, redirectFor, isNoindex };
